import React, { useEffect, useState } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import BypassCard from '../../../../components/Cards/BypassCard'; // Adjust the import path as needed
import { themes } from 'src/theme/theme';
import profileIcon from 'src/assets/profile-3x.png';
import Button from 'src/components/Button/Button';
import plusIcon from 'src/assets/plux-3x.png';
import TourWrapper from 'src/components/TourWrapper/TourWrapper';
import { useTourAutoStart } from 'src/features/tour/hooks/useTourAutoStart';
import { getDriverBypassTour } from 'src/features/tour/tourSteps';
import { useLinkTo } from '@react-navigation/native';
import { RootState } from 'src/store/store';
import { useSelector, useDispatch } from 'react-redux';
import {
  useDeleteDriverByIdMutation,
  useGetAllDriversQuery,
} from 'src/features/drivers/api/driverApi';
import {
  setCurrentDriver,
  setAllDrivers,
  toggleDriverToast,
} from 'src/features/drivers/slice/driverSlice';
import { setActiveTab, setFilledTabsIndex } from 'src/slices/UiSlice';
import Toast from 'src/components/Toast/Toast';
import { URLS } from 'src/constants/urls';
import { TABS_INDEX } from 'src/constants/tabs';
import { toggleToastState } from 'src/helpers/toastHelper';
import { trackGTMEvent } from 'src/helpers/gtm/gtmEvents'; // Import GTM helper method

const DriverBypass: React.FC = () => {
  const linkTo = useLinkTo();
  const dispatch = useDispatch();

  const drivers = useSelector((state: RootState) => state.drivers.drivers);
  const currentCompany = useSelector((state: RootState) => state.company.company);
  const accountType = useSelector((state: RootState) => state.UI.accountType);

  const showDriverToast = useSelector((state: RootState) => state.drivers.showDriverToast);
  const [deleteDriver] = useDeleteDriverByIdMutation();
  const [driverDeleteToast, setDriverDeleteToast] = useState(false);
  const [driverDeleteFailToast, setDriverDeleteFailToast] = useState(false);
  const {
    data: updateDrivers,
    isSuccess,
    refetch,
  } = useGetAllDriversQuery(currentCompany?.id || 0); //company id will be passed dynamically when company wireup is finished

  // Track Driver Summary Page Landed event
  useEffect(() => {
    trackGTMEvent(
      'driver-summary-page-landed',
      {
        userEvent: 'User landed on the Driver Summary page after filling out driver details.',
      },
      accountType,
    );
  }, []);

  // Auto-start tour on first visit
  useTourAutoStart({
    tourKey: 'driverBypass',
    driverSteps: getDriverBypassTour(),
    enabled: isSuccess && updateDrivers !== undefined,
  });

  useEffect(() => {
    if (isSuccess) {
      dispatch(setAllDrivers(updateDrivers));
      dispatch(setCurrentDriver({}));
    }
  }, []);

  useEffect(() => {
    if (showDriverToast) {
      refetch();
      // Hide the Toast after a delay
      const timeout = setTimeout(() => {
        dispatch(toggleDriverToast(false));
      }, 3000);
      return () => clearTimeout(timeout); // Cleanup timeout
    }
  }, [showDriverToast, dispatch]);

  const handleDelete = async (driverId: number) => {
    try {
      await deleteDriver(driverId).unwrap();

      // Track Driver Deleted event
      trackGTMEvent(
        'driver-deleted',
        {
          userEvent: 'User deleted a driver from the summary screen.',
          driverId,
        },
        accountType,
      );

      setDriverDeleteToast(true);
      toggleToastState(setDriverDeleteToast);
    } catch (error) {
      setDriverDeleteFailToast(true);
      toggleToastState(setDriverDeleteFailToast);
      console.error('Failed to delete driver:', error);
    }
  };

  const handleEdit = (driverId: number, index: number) => {
    // Track Driver Edited event
    trackGTMEvent(
      'driver-edited',
      {
        userEvent: 'User clicked edit on a driver and navigated to Add Driver details screen.',
        driverId,
      },
      accountType,
    );

    dispatch(setCurrentDriver(drivers[index]));
    linkTo(`${URLS.DRIVER.LICENSE}/${driverId}`);
  };

  const handleAddDriver = () => {
    // Track Another Driver Added event
    trackGTMEvent(
      'add-driver-button-pressed',
      {
        buttonTitle: 'Add another driver',
        userEvent: 'User clicked Add Driver button and navigated to the Add Driver screen.',
      },
      accountType,
    );

    linkTo(URLS.DRIVER.LICENSE);
  };

  const handleBack = () => {
    // Track Driver Summary Page Left event
    trackGTMEvent(
      'driver-summary-back-button-pressed',
      {
        buttonTitle: 'Back',
        userEvent: 'User clicked Back and navigated to the Driver Details screen',
      },
      accountType,
    );

    linkTo(URLS.VEHICLE.BYPASS);
    dispatch(setActiveTab({ activeTab: TABS_INDEX.VEHICLE }));
  };

  const handleNext = () => {
    // Track Driver Summary Confirmed event
    trackGTMEvent(
      'driver-summary-confirmed',
      {
        buttonTitle: 'Next',
        userEvent: 'User clicked Next and navigated to the Add Commodities screen',
      },
      accountType,
    );

    dispatch(setActiveTab({ activeTab: TABS_INDEX.FREIGHT }));
    dispatch(setFilledTabsIndex({ filledTabsIndex: TABS_INDEX.FREIGHT }));
    linkTo(URLS.FREIGHT);
  };

  return (
    <ScrollView
      contentContainerStyle={styles.container}
      keyboardShouldPersistTaps="handled"
      bounces={false}>
      <View style={styles.cardContainer}>
        <TourWrapper tourId="driver-list">
          <View>
            {drivers.length > 0 &&
              drivers.map((driver, index) => (
                <TourWrapper
                  key={driver.id}
                  tourId={index === 0 ? 'driver-card-actions' : undefined}>
                  <BypassCard
                    testID={`driver-bypass-item-card-${index}`}
                    imageSrc={profileIcon}
                    imageWidth={32}
                    imageHeight={32}
                    title={driver.firstName + ' ' + driver.lastName}
                    description={driver.dlState as string}
                    onEdit={() => handleEdit(driver.id as number, index)}
                    onDelete={() => handleDelete(driver.id as number)}
                  />
                </TourWrapper>
              ))}
          </View>
        </TourWrapper>
        <TourWrapper tourId="add-driver-btn">
          <Button
            testID="driver-bypass-add-another-driver"
            title="Add another driver"
            type="tertiary"
            colorVariant="default"
            icon={plusIcon}
            iconWidth={17}
            iconHeight={17}
            onPress={handleAddDriver}
          />
        </TourWrapper>
      </View>
      <View>
        {showDriverToast && (
          <Toast text="Details for this driver have been saved successfully." animate={true} />
        )}
        {driverDeleteToast && <Toast text="The selected driver has been deleted." animate={true} />}
        {driverDeleteFailToast && (
          <Toast
            text="An error occurred while deleting the selected driver. Please try again later."
            type="error"
            animate={true}
          />
        )}
        <View style={styles.buttonContainer}>
          <View style={styles.buttonWrapper1}>
            <Button
              type="tertiary"
              title="Back"
              onPress={handleBack}
              testID={'driver-bypass-add-driver-back-btn'}
            />
          </View>
          <View style={styles.buttonWrapper2}>
            <Button
              testID={'driver-bypass-add-driver-next-btn'}
              type="primary"
              colorVariant={drivers.length === 0 ? 'disabled' : 'default'}
              title="Next"
              onPress={handleNext}
            />
          </View>
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: `#FFF`,
  },
  cardContainer: {
    backgroundColor: themes.light.gray_blue[7],
    padding: 16,
    gap: 12,
    flex: 1,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between', // Space between buttons
    gap: 8,
    width: '100%',
    padding: 16,
  },
  buttonWrapper1: {
    flex: 1, // Allow it to grow
    maxWidth: '33%', // Restrict to 33% width
  },
  buttonWrapper2: {
    flex: 1, // Allow it to grow
    maxWidth: '67%', // Restrict to 67% width
  },
});

export default DriverBypass;
